import { useTranslation } from "next-i18next";

import { IconDeliveryWhoppahCourierOutline } from "@/assets/icons/figma/IconDeliveryWhoppahCourierOutline";
import { cn } from "@/common/utils/css.utils";
import { formatPostalCode } from "@/components/common/address/postalCode.format";
import { useDeliveryLocation } from "@/features/delivery-location/hooks/useDeliveryLocation";
import { Body } from "@/ui/typography/Body";
import { Hyperlink } from "@/ui/typography/Hyperlink";
import NoSSR from "@mpth/react-no-ssr";

type DeliveryLocationProps = {
  /** "header" is the icon + "Ship to:" row, "pdp" the labelled shipping-block row. */
  variant?: "header" | "pdp";
  className?: string;
};

/**
 * WHO-5226. The global delivery location, shown in the desktop header, at the
 * top of the mobile menu and in the PDP shipping block. Clicking it opens the
 * address modal; saving there updates every instance at once.
 */
export const DeliveryLocation = ({
  variant = "header",
  className,
}: DeliveryLocationProps) => {
  const { t } = useTranslation();
  const { address, openAddressModal } = useDeliveryLocation();

  const postalCodeAndCity =
    address &&
    [formatPostalCode(address.postal_code, address.country), address.city]
      .filter(Boolean)
      .join(", ");

  const label =
    variant === "pdp" && address?.line1
      ? [address.line1, postalCodeAndCity].join(", ")
      : postalCodeAndCity;

  /**
   * The address lives in a cookie the server does not see, so rendering it
   * during SSR would hydrate against the wrong text (and bake one visitor's
   * postcode into a cacheable page). The static label around it still renders
   * server-side, so only the address itself appears on mount.
   */
  const link = (
    <NoSSR>
      <Hyperlink
        as="button"
        type="button"
        className="truncate text-sm font-bold"
        onClick={openAddressModal}
      >
        {label || t("common:common-add-address")}
      </Hyperlink>
    </NoSSR>
  );

  if (variant === "pdp") {
    return (
      <div
        data-testid="pdp.shipping.address-select"
        className={cn("flex min-w-0 flex-col items-start", className)}
      >
        <Body as="span" className="text-sm">
          {t("pdp:pdp-usp-shipping-selected-address-title")}
        </Body>
        {link}
      </div>
    );
  }

  return (
    <div className={cn("flex min-w-0 items-center gap-1.5", className)}>
      <IconDeliveryWhoppahCourierOutline className="size-6 shrink-0" />
      <span className="shrink-0 font-body text-sm">
        {t("common:common-ship-to", "Ship to:")}
      </span>
      {link}
    </div>
  );
};
